import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import 'dayjs/locale/fr.js'

import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Space,
} from 'antd'
import { FormInstance } from 'antd/es/form'

import { ContactContext } from 'contexts/contactContext'
import { CompanyStaffsContext } from 'contexts/CompanyStaffsContext'

import {
  COMPANY_STAFF_CREATE,
  COMPANY_STAFF_UPDATE,
  COMPANY_STAFF_DESTROY,
} from 'graphql/CompanyStaffs'
import { GREFFES_QUERY } from 'graphql/Greffes'
import CentredLoader from 'components/CentredLoader'

import { CompanyStaffType, GreffeType } from 'types'
import { CompanyStaffArguments } from 'types/compagnyStaff'
import { CONTACT_LEGAL_STATUSES_OPTIONS_QUERY } from 'graphql/LegalStatuses'

interface IProps {
  toggleEditMode?: () => void
  staff?: CompanyStaffType
}

interface ICompanyStaffCreateInput {
  companyStaff: CompanyStaffArguments
}

interface ICompanyStaffUpdateInput {
  id: string
  companyStaff: CompanyStaffType
}

interface ILegalStatusOption {
  id: string
  name: string
}

type IFormValues = CompanyStaffArguments

export const StaffBoxCardEdit = (props: IProps) => {
  const { t } = useTranslation('companyStaffs')
  const { toggleEditMode, staff } = props
  const [form] = Form.useForm<IFormValues>()
  const formRef = useRef() as React.MutableRefObject<FormInstance<IFormValues>>
  const roles = [
    'leader',
    'mandataire',
    'host',
    'director',
    'comanager',
    'shareholder',
  ]
  const [siren, setSiren] = useState(staff?.siren)
  const [role, setRole] = useState(staff?.role || roles[0])
  const prefixes = ['Mr', 'Mme']
  const isCreation = !staff

  const { contact } = useContext(ContactContext)
  const { refetch } = useContext(CompanyStaffsContext)
  const resGreffes = useQuery(GREFFES_QUERY)
  const resLegalStatus = useQuery(CONTACT_LEGAL_STATUSES_OPTIONS_QUERY, {
    variables: {
      countryConfigId: contact.countryConfig.id,
    },
  })
  const [createCompanyStaffMutation] = useMutation(COMPANY_STAFF_CREATE)
  const [updateCompanyStaffMutation] = useMutation(COMPANY_STAFF_UPDATE)
  const [destroyCompanyStaffMutation] = useMutation(COMPANY_STAFF_DESTROY)

  if (resGreffes.loading) return <CentredLoader />

  const initialValues = {
    ...staff,
    birthDate: staff?.birthDate ? dayjs(staff.birthDate, 'YYYY/MM/DD') : null,
    greffeId: staff?.greffe?.id,
    legalStatusId: staff?.legalStatus?.id,
    contactId: contact.id,
  }

  const onFinish = (values: IFormValues) => {
    values.birthDate &&
      (values.birthDate = dayjs(values.birthDate).format('YYYY-MM-DD'))
    values.legalStatusId &&
      (values.legalStatusId = values.legalStatusId?.toString())
    if (isCreation) {
      const variables: ICompanyStaffCreateInput = {
        companyStaff: { ...values },
      }
      variables.companyStaff.contactId = contact.id
      createCompanyStaffMutation({ variables }).then(() => refetch())
    } else {
      const variables: ICompanyStaffUpdateInput = {
        id: staff?.id.toString(),
        companyStaff: {
          ...values,
          id: staff?.id.toString(),
          contactId: contact.id,
        },
      }

      updateCompanyStaffMutation({
        variables: variables,
        onCompleted: () => toggleEditMode && toggleEditMode(),
      })
    }
  }

  const handleDelete = () => {
    deleteStaff()
  }

  const deleteStaff = () => {
    if (!staff) return

    const variables = { id: staff.id }
    destroyCompanyStaffMutation({ variables: variables }).then(() => refetch())
  }

  return (
    <Form
      form={form}
      ref={formRef}
      name={`CompanyStaffEdit-${staff?.id}`}
      initialValues={initialValues}
      onFinish={onFinish}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item name="role" label={t(`attributes.role`)}>
        <Radio.Group>
          {roles.map((role: string) => {
            return (
              <Radio key={`roleOption-${role}`} value={role}>
                {t(`roles.${role}`)}
              </Radio>
            )
          })}
        </Radio.Group>
      </Form.Item>
      <Form.Item name="prefix" label={t(`attributes.prefix`)}>
        <Radio.Group>
          {prefixes.map((prefixe: string) => {
            return (
              <Radio key={`prefixOption-${prefixe}`} value={prefixe}>
                {t(`prefixes.${prefixe}`)}
              </Radio>
            )
          })}
        </Radio.Group>
      </Form.Item>
      <Form.Item name="firstname" label={t('attributes.firstname')}>
        <Input />
      </Form.Item>
      <Form.Item name="lastname" label={t('attributes.lastname')}>
        <Input />
      </Form.Item>
      <Form.Item name="birthDate" label={t('attributes.birthDate')}>
        <DatePicker format={'DD/MM/YYYY'} />
      </Form.Item>

      {/*BIRTH PLACE*/}
      <Form.Item label={'Lieu de naissance'}>
        <Space.Compact>
          <Form.Item
            name={['birthPlace', 'city']}
            label={t('attributes.birthPlace.city')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['birthPlace', 'postal']}
            label={t('attributes.birthPlace.postal')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['birthPlace', 'country']}
            label={t('attributes.birthPlace.country')}
          >
            <Input />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      <Form.Item name="birthCountry" label={t('attributes.birthCountry')}>
        <Input />
      </Form.Item>
      <Form.Item name="nationality" label={t('attributes.nationality')}>
        <Input />
      </Form.Item>
      <Form.Item
        name="socialSecurityNumber"
        label={t('attributes.socialSecurityNumber')}
      >
        <Input />
      </Form.Item>
      <Form.Item name="postalAddress" label={t('attributes.postalAddress')}>
        <Input />
      </Form.Item>
      <Form.Item name={'hiddenAddress'} valuePropName="checked">
        <Checkbox>{'Confidentielle'}</Checkbox>
      </Form.Item>
      <Form.Item name="emailAddress" label={t('attributes.emailAddress')}>
        <Input />
      </Form.Item>
      <Form.Item name="maritalStatus" label={t('attributes.maritalStatus')}>
        <Input />
      </Form.Item>

      {/*FATHER*/}
      <Form.Item label={'Père'}>
        <Form.Item name={['father', 'unknown']} valuePropName="checked">
          <Checkbox>{'Inconnu'}</Checkbox>
        </Form.Item>
        <Space.Compact>
          <Form.Item name={['father', 'prefix']} label={t('attributes.prefix')}>
            <Radio.Group>
              {prefixes.map((prefixe: string) => {
                return (
                  <Radio key={`prefixOption-father-${prefixe}`} value={prefixe}>
                    {t(`prefixes.${prefixe}`)}
                  </Radio>
                )
              })}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name={['father', 'first']}
            label={t('attributes.firstname')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['father', 'last']}
            label={t('attributes.father.lastname')}
          >
            <Input />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      {/*MOTHER*/}
      <Form.Item label={'Mère'}>
        <Form.Item name={['mother', 'unknown']} valuePropName="checked">
          <Checkbox>{'Inconnue'}</Checkbox>
        </Form.Item>
        <Space.Compact>
          <Form.Item name={['mother', 'prefix']} label={t('attributes.prefix')}>
            <Radio.Group>
              {prefixes.map((prefixe: string) => {
                return (
                  <Radio key={`prefixOption-mother-${prefixe}`} value={prefixe}>
                    {t(`prefixes.${prefixe}`)}
                  </Radio>
                )
              })}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name={['mother', 'first']}
            label={t('attributes.firstname')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['mother', 'last']}
            label={t('attributes.mother.lastname')}
          >
            <Input />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      {/* MORAL */}
      <Form.Item name="siren" label={t('attributes.siren')}>
        <Input onChange={e => setSiren(e.target.value)} />
      </Form.Item>
      {siren && (
        <>
          <Form.Item
            name="socialDenomination"
            label={t('attributes.socialDenomination')}
          >
            <Input />
          </Form.Item>
          <Form.Item name="socialCapital" label={t('attributes.socialCapital')}>
            <Input />
          </Form.Item>
          <Form.Item name="socialPlace" label={t('attributes.socialPlace')}>
            <Input />
          </Form.Item>
          <Form.Item name="greffeId" label={t(`attributes.greffe`)}>
            {resGreffes.data?.greffes && (
              <Select>
                {[...resGreffes.data.greffes]
                  .sort((a: GreffeType, b: GreffeType) =>
                    a.name.localeCompare(b.name)
                  )
                  .map((greffe: GreffeType) => {
                    return (
                      <Select.Option
                        key={`greffeOption-${greffe.id}`}
                        value={greffe.id}
                      >
                        {greffe.name}
                      </Select.Option>
                    )
                  })}
              </Select>
            )}
          </Form.Item>
          <Form.Item name="legalStatusId" label={t(`attributes.legalStatus`)}>
            {resLegalStatus.data?.legalStatuses && (
              <Select>
                {[...resLegalStatus.data?.legalStatuses].map(
                  (legalStatus: ILegalStatusOption) => {
                    return (
                      <Select.Option
                        key={`legalStatusOption-${legalStatus.id}`}
                        value={legalStatus.id}
                      >
                        {legalStatus.name}
                      </Select.Option>
                    )
                  }
                )}
              </Select>
            )}
          </Form.Item>
        </>
      )}
      {/* Role's field*/}
      {role === 'shareholder' && (
        <Form.Item name={'shares'} label={t('attributes.shares')}>
          <InputNumber />
        </Form.Item>
      )}
      {/* BTNS */}
      <Form.Item label=" ">
        <Button type="primary" htmlType="submit">
          {isCreation ? t('submit.create') : t('submit.update')}
        </Button>
      </Form.Item>

      {!isCreation && (
        <Popconfirm
          title={t('confirm.delete.title')}
          description={t('confirm.delete.description')}
          onConfirm={handleDelete}
          okText={t('confirm.yes')}
          cancelText={t('confirm.no')}
        >
          <Button danger>{t('confirm.delete.label')}</Button>
        </Popconfirm>
      )}
    </Form>
  )
}
